import routes from 'js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';

// The shared window.loswp.work type is deliberately { id } only
// (single_work/loswp/types.ts). The runtime payload is the RAW Work row
// (show.html.slim serializes the controller's @work model, not
// SingleWorkSerializer), so attachment data is never present — page count
// comes from the page markup instead. Cast locally, never widen the type.
type LoswpWork = {
  id: number;
  title?: string;
  summary?: string;
  owner_type?: string;
  downloadable_attachment?: { page_count?: number } | null;
};

export type WorkContext = {
  workId: number;
  title?: string;
  summary?: string;
  ownerType?: string;
  pageCount?: number;
  thumbnailUrl?: string;
  takeaways: string[];
  figureCount?: number;
  referenceCount?: number;
};

const STAT_FETCH_TIMEOUT_MS = 800;

let cachedContext: WorkContext | null | undefined;
const downloadCountRequests = new Map<number, Promise<number | null>>();
const percentileRequests = new Map<number, Promise<string | null>>();

function loswpWork(): LoswpWork | null {
  const work = (window as unknown as { loswp?: { work?: LoswpWork } }).loswp
    ?.work;
  return typeof work?.id === 'number' ? work : null;
}

export function loswpWorkId(): number | null {
  return loswpWork()?.id ?? null;
}

export function getWorkContext(): WorkContext | null {
  if (cachedContext === undefined) {
    cachedContext = buildWorkContext();
  }
  return cachedContext;
}

// Page-init trail seed: skips the takeaways/figures/references scrapes and
// never touches cachedContext, so an early call can't freeze a page-load-time
// snapshot that open-time getWorkContext readers would then inherit.
export function getTrailSeed(): {
  workId: number;
  title?: string;
  thumbnailUrl?: string;
} | null {
  try {
    const work = loswpWork();
    if (!work) return null;
    return {
      workId: work.id,
      title: work.title,
      thumbnailUrl: scrapeThumbnailUrl(),
    };
  } catch {
    return null;
  }
}

export function resetWorkContextCache(): void {
  cachedContext = undefined;
  downloadCountRequests.clear();
  percentileRequests.clear();
}

// Each call races the (cached) request against a fresh timeout, so an open
// never waits more than the budget, while a slow first response can still
// serve a later open instead of being poisoned by the first race's null.
export function fetchDownloadCount(workId: number): Promise<number | null> {
  let request = downloadCountRequests.get(workId);
  if (!request) {
    request = fetchWithCsrf(
      routes.api_v0_work_download_count_path({
        work_id: workId,
        subdomain_param: 'api',
      }),
    )
      .then((response) => (response.ok ? response.json() : null))
      .then((data: { count?: number } | null) =>
        data && typeof data.count === 'number' ? data.count : null,
      )
      .catch(() => null);
    downloadCountRequests.set(workId, request);
  }

  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), STAT_FETCH_TIMEOUT_MS);
  });
  return Promise.race([request, timeout]).finally(() => clearTimeout(timer));
}

// Anon-safe: works below the 95th percentile are simply absent from the map,
// so a missing entry (like a non-ok/timed-out fetch) resolves to null. The
// endpoint takes work_ids as a comma-joined string, not a work_ids[] array.
export function fetchPercentile(workId: number): Promise<string | null> {
  let request = percentileRequests.get(workId);
  if (!request) {
    request = fetchWithCsrf(
      routes.api_v0_works_percentiles_path({
        work_ids: String(workId),
        subdomain_param: 'api',
      }),
    )
      .then((response) => (response.ok ? response.json() : null))
      .then((data: { percentiles?: Record<string, string> } | null) => {
        const text = data?.percentiles?.[String(workId)];
        return typeof text === 'string' && text.length > 0 ? text : null;
      })
      .catch(() => null);
    percentileRequests.set(workId, request);
  }

  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), STAT_FETCH_TIMEOUT_MS);
  });
  return Promise.race([request, timeout]).finally(() => clearTimeout(timer));
}

function buildWorkContext(): WorkContext | null {
  try {
    const work = loswpWork();
    if (!work) return null;

    return {
      workId: work.id,
      title: work.title,
      summary: work.summary,
      ownerType: work.owner_type,
      pageCount: work.downloadable_attachment?.page_count ?? scrapePageCount(),
      thumbnailUrl: scrapeThumbnailUrl(),
      takeaways: scrapeTakeaways(),
      figureCount: scrapeFigureCount(),
      referenceCount: scrapeReferenceCount(),
    };
  } catch {
    return null;
  }
}

// Both page designs put the count in the work card's metadata stat; the
// blank-cover variant (unconverted attachment) carries its own copy.
function scrapePageCount(): number | undefined {
  const nodes = document.querySelectorAll(
    '.ds-work-card--work-metadata__stat, .ds-work-cover--blank-cover--page-count',
  );
  for (const node of Array.from(nodes)) {
    const match = node.textContent?.match(/(\d[\d,]*) pages?/);
    if (match) return Number(match[1].replace(/,/g, ''));
  }
  return undefined;
}

function scrapeThumbnailUrl(): string | undefined {
  const image = document.querySelector<HTMLImageElement>(
    'img.ds-work-cover--cover-thumbnail',
  );
  return image?.src || undefined;
}

function scrapeTakeaways(): string[] {
  return Array.from(document.querySelectorAll('.key-takeaways--list li'))
    .map((item) => item.textContent?.trim() ?? '')
    .filter((text) => text.length > 0);
}

function scrapeFigureCount(): number | undefined {
  const count = document.querySelectorAll('.figure-slide-container').length;
  return count > 0 ? count : undefined;
}

// Outline-design markup only; the ungated design renders references through
// a different card component and degrades to undefined here.
function scrapeReferenceCount(): number | undefined {
  const heading = document.querySelector('.references--heading');
  const match = heading?.textContent?.match(/References \(([\d,]+)\)/);
  return match ? Number(match[1].replace(/,/g, '')) : undefined;
}
